import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Loader2, Lock } from 'lucide-react';
// Import centralized configuration
import { secureFetch, ROUTES } from "../utils/apiClient";

const protocols = [
  {
    question: "What is the scope of Micheals Infrastructure Advisory?",
    answer: "We function exclusively as technical stewards and forensic infrastructure architects. We do not perform field labor; we provide the strategic governance required to eliminate project risk and maximize asset yield."
  },
  {
    question: "Define the Forensic Infrastructure Audit.",
    answer: "A deep-dive technical reconciliation of your built environment. We identify latent vulnerabilities, logic gaps, and systemic inefficiencies before they translate into capital loss or operational downtime."
  },
  {
    question: "Do you facilitate federal and government compliance?",
    answer: "Yes. We architect the technical documentation and regulatory framework necessary for SAM.gov, CAGE, and UEI compliance, ensuring your infrastructure meets stringent federal standards."
  },
  {
    question: "How does the firm manage fiduciary risk?",
    answer: "We operate with total design intent transparency. By identifying failure points during the architectural phase, we protect your capital and ensure the long-term integrity of your infrastructure assets."
  }
];

const engagementTiers = [
  {
    name: 'Preliminary Briefing',
    id: 'Option_B',
    price: '$150',
    amount: 150,
    description: 'Technical scope alignment and initial infrastructure integrity assessment.',
    features: ['Technical Integrity Check', 'Project Roadmap', 'Strategic Session'],
    actionLabel: 'Initialize Protocol'
  },
  {
    name: 'Forensic Audit',
    id: 'Option_Audit',
    price: '$450',
    amount: 450,
    description: 'Comprehensive forensic analysis of operational friction and system risk.',
    features: ['Vulnerability Assessment', 'Compliance Reporting', 'Forensic Documentation'],
    actionLabel: 'Secure Audit'
  },
  {
    name: 'Architectural Retainer',
    id: 'Option_Retainer',
    price: '$2,500',
    amount: 2500,
    description: 'Ongoing principal-level strategic oversight and system logic governance.',
    features: ['Principal Consultation', 'Asset Stewardship', 'Priority Governance'],
    highlight: true,
    actionLabel: 'Engage Stewardship'
  },
];

const FAQ = ({ sessionId, leadEmail }) => {
  const [loadingTier, setLoadingTier] = useState(null);
  const [error, setError] = useState(null);

  const handleAction = async (tier) => {
    setLoadingTier(tier.id);
    setError(null);
    
    try {
      // Use the unified secureFetch and centralized routing from ROUTES
      const response = await secureFetch(ROUTES.CREATE_PAYMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          tier: tier.id,
          amount: tier.amount, 
          email: leadEmail || "info@medconsulting.solutions",
          sessionId: sessionId 
        })
      });

      if (response && response.url) {
        window.location.assign(response.url);
      } else {
        throw new Error("Invalid Engagement Protocol");
      }
    } catch (err) {
      console.error("❌ [FORENSIC_LINK_FAILURE]:", err);
      setError(`Consultation Gateway Error: ${err.message}`);
      setLoadingTier(null);
    }
  };

  return (
    <div className="bg-stone-50 min-h-screen pt-32 pb-20 px-6 font-sans">
      
      {/* SECTION 1: GOVERNANCE BRIEFING */}
      <section className="max-w-4xl mx-auto mb-32">
        <div className="mb-16">
          <h2 className="text-4xl font-serif italic text-stone-950 mb-4 tracking-tighter">
            Governance Briefing
          </h2>
          <div className="h-px w-20 bg-amber-700"></div>
        </div>

        <div className="grid gap-12">
          {protocols.map((protocol, index) => (
            <motion.div 
              key={index} 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="border-l border-stone-200 pl-8 py-2"
            >
              <h3 className="text-[10px] font-black text-stone-950 uppercase tracking-[0.2em] mb-3">
                {protocol.question}
              </h3>
              <p className="text-stone-600 leading-relaxed font-light text-sm">
                {protocol.answer}
              </p>
            </motion.div>
          ))}
        </div>
      </section>
      
      {/* SECTION 2: CAPITAL ENGAGEMENT PROTOCOLS */}
      <section className="max-w-7xl mx-auto border-t border-stone-200 pt-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-serif italic text-stone-950 mb-4 tracking-tighter">
            Capital Engagement Protocols
          </h2>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-800">
            Fiduciary Asset Stewardship
          </p>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="max-w-md mx-auto mb-10 p-4 bg-red-50 border border-red-200 rounded-sm flex items-center gap-4 text-red-800 text-xs"
            >
              <ShieldAlert className="shrink-0" size={18} />
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid md:grid-cols-3 gap-8">
          {engagementTiers.map((tier, index) => (
            <motion.div 
              key={index} 
              whileHover={{ y: -5 }}
              className={`flex flex-col p-10 rounded-sm border transition-all duration-500 ${
                tier.highlight 
                  ? 'border-amber-700 bg-white shadow-xl' 
                  : 'border-stone-200 bg-white'
              }`}
            >
              <h3 className="text-[10px] font-black text-stone-950 uppercase tracking-[0.2em] mb-6">
                {tier.name}
              </h3>
              <div className="flex items-baseline gap-2 mb-6 text-stone-950">
                <span className="text-4xl font-black font-serif">{tier.price}</span>
                <span className="text-stone-400 text-[10px] font-bold uppercase">/ USD</span>
              </div>
              <p className="text-stone-500 text-xs leading-relaxed mb-8 h-12">
                {tier.description}
              </p>
              
              <ul className="text-stone-900 space-y-4 mb-10 text-[10px] font-black uppercase tracking-widest flex-grow">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-amber-700" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => handleAction(tier)}
                disabled={loadingTier !== null}
                className={`w-full py-4 text-[10px] font-black uppercase tracking-[0.3em] transition-all duration-300 flex items-center justify-center gap-2 ${
                  loadingTier === tier.id ? 'opacity-50 cursor-not-allowed' : ''
                } ${
                  tier.highlight 
                    ? 'bg-stone-950 text-white hover:bg-amber-800' 
                    : 'border border-stone-950 text-stone-950 hover:bg-stone-950 hover:text-white'
                }`}
              >
                {loadingTier === tier.id ? (
                  <><Loader2 className="animate-spin" size={14} /> Authorizing</>
                ) : (
                  <><Lock size={12} /> {tier.actionLabel}</>
                )}
              </button>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default FAQ;